import { Box, Grid, styled, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { useMortgageState } from "../../../contexts/mortgage/Reducer";
import { DateFormat, formatDate } from "../../../utils/DateUtils";
import { formatAmount, parseIBAN } from "../../../utils/NumberUtils";
import ErrorMessage from "../../feedback/ErrorMessage";

const MortgageProvisionsHeaderLabel = styled(Typography)(() => ({
    color: "rgba(0,0,0,0.54)",
    fontWeight: 400,
    fontSize: "14px",
    letterSpacing: 0,
    lineHeight: "18px",
}));

const MortgageProvisionsLabel = styled(Typography)(() => ({
    color: "#333333",
    fontSize: "18px",
    letterSpacing: 0,
    lineHeight: "26px",
    fontWeight: 300,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
}));

const ProvisionsNoteWrapper = styled(Box)(({ theme }) => ({
    marginTop: "32px",
    marginBottom: "32px",
    marginLeft: "24px",
    [theme.breakpoints.up("lg")]: {
        marginTop: "40px",
    },
}));

const MortgageGeneralProvisions = () => {
    const { t } = useTranslation();

    const { newMortgage } = useMortgageState();

    if (newMortgage === undefined) {
        return <ErrorMessage />;
    }

    return (
        <Grid container spacing={3} data-cy={"general-provision"}>
            <Grid container item sm={12} md={12} lg={5} spacing={3}>
                <Grid item sm={12} md={12}>
                    <Box sx={{ paddingBottom: "4px" }}>
                        <MortgageProvisionsHeaderLabel data-cy={"borrower-label"}>
                            {t("SummaryScreen.Borrower")}
                        </MortgageProvisionsHeaderLabel>
                    </Box>
                    <Box>
                        <MortgageProvisionsLabel data-cy={"borrower"}>{newMortgage.ownerName}</MortgageProvisionsLabel>
                    </Box>
                </Grid>
                <Grid item sm={12} md={12}>
                    <Box sx={{ paddingBottom: "4px" }}>
                        <MortgageProvisionsHeaderLabel data-cy={"credit-agreement-text"}>
                            {t("SummaryScreen.Master credit agreement")}
                        </MortgageProvisionsHeaderLabel>
                    </Box>
                    <Box>
                        <MortgageProvisionsLabel data-cy={"expiration-date"}>
                            {formatDate(newMortgage.signatureDate, DateFormat.DATE)}
                        </MortgageProvisionsLabel>
                    </Box>
                </Grid>
                <Grid item sm={12} md={12}>
                    <Box sx={{ paddingBottom: "4px" }}>
                        <MortgageProvisionsHeaderLabel data-cy={"new-mortgages-text"}>
                            {t("SummaryScreen.New mortgage(s)")}
                        </MortgageProvisionsHeaderLabel>
                    </Box>
                    <Box>
                        <MortgageProvisionsLabel data-cy={"general-provision-mortgage-amount"}>
                            {formatAmount(newMortgage.amount)}
                        </MortgageProvisionsLabel>
                    </Box>
                </Grid>
                <Grid item sm={12} md={12}>
                    <Box sx={{ paddingBottom: "4px" }}>
                        <MortgageProvisionsHeaderLabel data-cy={"interest-rate-text"}>
                            {t("SummaryScreen.Interest rate 2")}
                        </MortgageProvisionsHeaderLabel>
                    </Box>
                    <Box>
                        <MortgageProvisionsLabel data-cy={"swiss-term-text"}>
                            {t("SummaryScreen.Swiss term (30/360)")}
                        </MortgageProvisionsLabel>
                    </Box>
                </Grid>
            </Grid>
            <Grid container item sm={12} md={12} lg={5} spacing={3}>
                <Grid item sm={12} md={12}>
                    <Box sx={{ paddingBottom: "4px" }}>
                        <MortgageProvisionsHeaderLabel data-cy={"capital-account-text"}>
                            {t("SummaryScreen.Capital account")}
                        </MortgageProvisionsHeaderLabel>
                    </Box>
                    <Box>
                        <MortgageProvisionsLabel data-cy={"position-capital"}>
                            {newMortgage.positionCapital}
                        </MortgageProvisionsLabel>
                        <MortgageProvisionsLabel data-cy={"iban-capital"}>
                            {parseIBAN(newMortgage.ibanCapital)}
                        </MortgageProvisionsLabel>
                    </Box>
                </Grid>
                <Grid item sm={12} md={12}>
                    <Box sx={{ paddingBottom: "4px" }}>
                        <MortgageProvisionsHeaderLabel data-cy={"interest-account"}>
                            {t("SummaryScreen.Interest account")}
                        </MortgageProvisionsHeaderLabel>
                    </Box>
                    <Box>
                        <MortgageProvisionsLabel data-cy={"position-interest"}>
                            {newMortgage.positionInterest}
                        </MortgageProvisionsLabel>
                        <MortgageProvisionsLabel data-cy={"iban-interest"}>
                            {parseIBAN(newMortgage.ibanInterest)}
                        </MortgageProvisionsLabel>
                    </Box>
                </Grid>
                <Grid item sm={12} md={12}>
                    <Box sx={{ paddingBottom: "4px" }}>
                        <MortgageProvisionsHeaderLabel data-cy={"interest-payment-date-text"}>
                            {t("SummaryScreen.Interest payment date")}
                        </MortgageProvisionsHeaderLabel>
                    </Box>
                    <Box>
                        <MortgageProvisionsLabel data-cy={"interest-rate-frequency"}>
                            {newMortgage.interestRateFrequency}
                        </MortgageProvisionsLabel>
                    </Box>
                </Grid>
            </Grid>
            <ProvisionsNoteWrapper>
                <MortgageProvisionsHeaderLabel data-cy={"borrower"}>
                    {t("SummaryScreen.The master credit agreement applies to this new product agreement.")}
                </MortgageProvisionsHeaderLabel>
            </ProvisionsNoteWrapper>
        </Grid>
    );
};

export default MortgageGeneralProvisions;
